import { FeatureFlagOverrides, LaunchDarklyService } from '@ampcore/client-side-services';
import { useEffect, useState } from 'react';
/**
 * Hook to access a LaunchDarkly feature flag value with real-time updates.
 *
 * `isReady` turns true once the flag read has settled — whether LaunchDarkly
 * initialized in time or the wait timed out. On timeout `flagValue` is the
 * supplied default, which is the intended value when the service cannot be
 * reached; consumers act on `flagValue` alone and need not distinguish the two.
 */
export function useFeatureFlag(flagKey, defaultValue) {
    const ldService = LaunchDarklyService.getInstance();
    const [flagValue, setFlagValue] = useState(defaultValue);
    const [isReady, setIsReady] = useState(false);
    useEffect(() => {
        const override = FeatureFlagOverrides.get(flagKey);
        if (override !== undefined) {
            setFlagValue(override);
            setIsReady(true);
            return;
        }
        const ldClient = ldService.getClient();
        let isMounted = true;
        let updateFlag;
        ldService.waitForInitialization().then(() => {
            if (!isMounted)
                return;
            if (!ldClient) {
                setIsReady(true);
                return;
            }
            updateFlag = () => {
                const value = ldClient.variation(flagKey, defaultValue);
                setFlagValue(value);
            };
            updateFlag();
            setIsReady(true);
            ldClient.on(`change:${flagKey}`, updateFlag);
        });
        return () => {
            isMounted = false;
            if (ldClient && typeof ldClient.off === 'function' && updateFlag) {
                ldClient.off(`change:${flagKey}`, updateFlag);
            }
        };
    }, [ldService, flagKey, defaultValue]);
    return { flagValue, isReady };
}
